/**
 * The reads and writes the Tax Profile page makes against /api/taxProfile, built the way
 * useAccountData.ts builds its reads: one function per route, with each route's SWR key made in one
 * place so a write can refresh exactly the read it changed.
 *
 * Returns, from the default export useTaxProfile, one function per route. The four getters hand
 * back SWR's own { data, error }; the writers return what the api answered and throw whatever
 * utils/api.ts threw, so a caller's toast and its 409 handling still work.
 *
 * A request the api refuses shows the person a toast, and for the answers reportableApiStatus below
 * picks out it sends one Rollbar item as well, so a route that is failing for everybody is not left
 * to whoever happens to mention it.
 */

import { useEffect, useState } from 'react';
import { useRollbar } from '@rollbar/react';
import { mutate } from 'swr';
import type { OcrDocumentType } from '@shared/constants/taxProfile';
import type { TaxProfileWritableKey } from '@shared/constants/taxProfileFields';
import type {
  AddTaxDocumentFromTeamDriveRequest,
  AddTaxDocumentSkipReason,
  AddTaxDocumentsResponse,
  AnswerTaxDocumentEntityRequest,
  ApplyTaxProfileRowRequest,
  EditTaxProfileFieldRequest,
  ReadTaxDocumentRequest,
  RemoveTaxProfilePersonRequest,
  RemoveTaxProfilePersonResponse,
  ReviewTaxProfileFieldsRequest,
  ReviewTaxProfileFieldsResponse,
  TaxDocumentListItem,
  TaxDocumentReviewResponse,
  TaxDocumentsResponse,
  TaxProfileFieldConflictResponse,
  TaxProfileFieldWriteResponse,
  TaxProfilePersonTable,
  TaxProfileResponse,
  UpdateTaxDocumentRequest,
} from '@shared/types/taxProfile';
import { get } from '@/utils/queries';
// The plain GET, imported beside the SWR-backed one above. The Source column's button asks for a
// document's address once, on a click, and nothing caches or reads it again.
import {
  fetchDelete,
  get as apiGet,
  patch,
  post,
  postFiles,
  put,
} from '@/utils/api';
import type { ApiRequestError } from '@/utils/api';

const baseRoute = 'taxProfile';

/** How often the documents list is fetched again while a document is still being read. */
export const DOCUMENTS_POLL_MS = 5000;

/**
 * How many of addFromTeamDrive's requests are in flight at once.
 *
 * Three rather than one, because a person who picked twenty files should not wait for twenty round
 * trips one after another; three rather than twenty, because each of those requests holds a file's
 * bytes in the api process while it copies them.
 */
export const TEAM_DRIVE_ADDS_AT_ONCE = 3;

/**
 * How many of uploadDocuments's requests are in flight at once.
 *
 * Two, because each of those requests carries a file of up to TAX_DOCUMENT_MAX_FILE_BYTES that the
 * api holds in memory (multer's memory storage) until the file is written to the team's drive, and
 * an api instance serves many people at once.
 */
export const TAX_DOCUMENT_UPLOADS_AT_ONCE = 2;

/**
 * How far one file of an addFromTeamDrive batch has got, handed to that function's onProgress
 * callback so a caller can mark this one file while the other files are still going.
 *
 * 'sending' says the file's request is about to leave the browser, 'added' says the route wrote a
 * row for it, and 'not_added' says it did not. `reason` carries the route's own skip reason, for a
 * file the route answered 409 for or named in the skipped list of its answer, and is left out where
 * the request was refused for some other reason.
 */
export type TeamDriveFileProgress = {
  itemId: string;
  state: 'sending' | 'added' | 'not_added';
  reason?: AddTaxDocumentSkipReason;
};

/**
 * How long a file's request waits before it is sent again when the answer has no Retry-After, one
 * entry per retry: 1 s, 3 s, 9 s, and 9 s for every retry after the third.
 *
 * A file is sent again only after a 429, which says the request was turned away before anything was
 * done with the file: by the instance in front of the api, or, for an upload from the computer, by
 * the upload route itself when an api process already holds TAX_DOCUMENT_UPLOADS_PER_INSTANCE
 * uploads (api/routers/taxProfileRouter.ts), which reads the body and discards it without
 * passing it to multer, then answers. A 503 is not sent again:
 * nothing in the api answers 503, so one comes from App Engine, which can send it after the api has
 * already copied or stored the file, and a second request would then add the file a second time.
 * How many times a file is sent again is set by TAX_DOCUMENT_MAX_BUSY_WAIT_MS.
 */
export const TAX_DOCUMENT_RETRY_DELAYS_MS = [1000, 3000, 9000];

/**
 * How long a file answered 429 keeps being sent again, counted from its first send, before it is
 * reported as failed ('busy') and the batch goes on: one minute. The time each send took counts as
 * well as the waits between them, because every retry sends the whole file again. The upload route
 * holds a slot from when a request reaches the api until the api lets go of the file, and another
 * file's upload can take a good part of a minute, so a file waiting for a slot may need about that
 * long. A retry whose wait would end past this is not made. Time rather than a count of retries,
 * because Retry-After decides each wait.
 */
export const TAX_DOCUMENT_MAX_BUSY_WAIT_MS = 60000;

/**
 * The longest a Retry-After header is waited for: 30 seconds. A larger one is cut to this, so one
 * answer cannot hold the batch, and the tab's "Leave site?" question, for minutes.
 */
export const TAX_DOCUMENT_MAX_RETRY_WAIT_MS = 30000;

/**
 * How long to wait before retry number `retry` (0 for the first): what the answer's Retry-After
 * header says, in seconds or as an HTTP date, and TAX_DOCUMENT_RETRY_DELAYS_MS when it has none.
 * Never less than the first of those delays, so a Retry-After of 0 cannot send a file again and
 * again at once.
 */
export const retryWaitMs = (
  retryAfter: string | null | undefined,
  retry: number
): number => {
  const fallback =
    TAX_DOCUMENT_RETRY_DELAYS_MS[
      Math.min(retry, TAX_DOCUMENT_RETRY_DELAYS_MS.length - 1)
    ];
  if (!retryAfter) return fallback;
  const seconds = Number(retryAfter);
  const asked = Number.isFinite(seconds)
    ? seconds * 1000
    : Date.parse(retryAfter) - Date.now();
  if (Number.isNaN(asked)) return fallback;
  return Math.min(
    Math.max(asked, TAX_DOCUMENT_RETRY_DELAYS_MS[0]),
    TAX_DOCUMENT_MAX_RETRY_WAIT_MS
  );
};

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Why a file of an add batch was not added although nothing refused it on purpose, which is what
 * keeps these out of `skipped` (already attached, a type we do not take, too large):
 *
 *  - 'connection_dropped': a file picked from the client's team got no answer. It is not retried,
 *    because a request that got no answer may still be running on the api: the copy into the Client
 *    Profile folder is written before the row, so a second request sent in that time finds no row
 *    and copies the file again, leaving two copies in OneDrive.
 *  - 'busy': a 429, including after the retries.
 *  - 'server_error': any 5xx, a 503 included, which is not retried: the api may have added the
 *    file before the answer was written, so the person checks the documents list and retries by
 *    hand. A 503 is reported here rather than as 'busy' because 'busy' is the reason a retry was
 *    safe and was tried.
 *  - 'upload_unconfirmed': a file from the person's computer got no answer. It is not retried,
 *    because the browser cannot tell whether the api stored the file and wrote its row before the
 *    connection dropped, and a second upload would be stored beside the first as "name 1".
 */
export type TaxDocumentAddFailureReason =
  | 'connection_dropped'
  | 'busy'
  | 'server_error'
  | 'upload_unconfirmed';

/**
 * What addFromTeamDrive and uploadDocuments answer with: the route's own `created` and `skipped`
 * added up over the batch, and the list the browser fills in itself.
 *
 * `failed` holds the files that went wrong in the way TaxDocumentAddFailureReason says. The other
 * files of the batch are still sent.
 */
export type AddTaxDocumentsOutcome = AddTaxDocumentsResponse & {
  failed: { name: string; reason: TaxDocumentAddFailureReason }[];
};

const failedOutcome = (
  name: string,
  reason: TaxDocumentAddFailureReason
): AddTaxDocumentsOutcome => ({
  created: [],
  skipped: [],
  failed: [{ name, reason }],
});

const answeredOutcome = (
  answer: AddTaxDocumentsResponse
): AddTaxDocumentsOutcome => ({
  ...answer,
  failed: [],
});

/**
 * What the browser's own "Leave site?" question is handed while files are still being sent. No
 * browser shows this text; it is set because older ones ask only when returnValue is a string.
 */
const FILES_STILL_BEING_SENT =
  'Files are still being added to the tax profile.';

/**
 * How many batches are sending right now. The listener is one shared function, and the browser
 * keeps a single registration of it, so the first batch adds it and only the last one to finish
 * removes it: a batch started on a fresh page while an earlier one is still sending keeps it.
 */
let batchesSending = 0;

/**
 * Asks the browser's own "Leave site?" question when the tab is closed or reloaded, or a link out
 * of the dashboard is followed. Modern browsers need preventDefault; older ones need returnValue.
 */
const askBeforeLeaving = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = FILES_STILL_BEING_SENT;
  return FILES_STILL_BEING_SENT;
};

/**
 * Sends one request per file, `atOnce` at a time, and adds the answers up into one, so the caller
 * shows one summary however many files it sent.
 *
 * `sendOne` makes one file's request, with its retries, and answers with that file's outcome; a
 * file the route refused for that file alone, or that failed, is answered by `sendOne` itself as a
 * skipped or failed entry. Anything `sendOne` throws is a refusal of the whole batch: no further
 * file is started, the files already in flight settle, and that refusal is thrown, exactly as a
 * single request would throw it. `onSettled` is called each time one file's outcome is known.
 *
 * From the first file until the last one settles, closing or reloading the tab asks the browser's
 * "Leave site?" question first, because a file not yet sent would be lost. The listener is owned
 * here rather than by the modal, so it lasts as long as the files are being sent even if the modal
 * is closed or the page is left for another page of the dashboard; this function's closure keeps
 * sending either way.
 */
async function sendOneRequestPerFile<T>(
  files: T[],
  atOnce: number,
  sendOne: (file: T) => Promise<AddTaxDocumentsOutcome>,
  onSettled?: () => void
): Promise<AddTaxDocumentsOutcome> {
  const outcome: AddTaxDocumentsOutcome = {
    created: [],
    skipped: [],
    failed: [],
  };
  const waiting = [...files];
  // The first refusal, exactly as it was thrown, so the caller's toast reads the api's own sentence
  // off it as it does for a single request. It is a list rather than one variable because it is
  // written inside the senders below and read after all of them have settled, which a variable
  // narrowed to its initial null would not allow.
  const refused: unknown[] = [];

  const sendInTurn = async () => {
    // Each sender takes the next file off the list until the list is empty or one of them has been
    // refused. It waits for each file on purpose, because that is what paces the requests.
    for (
      let next = waiting.shift();
      next !== undefined && refused.length === 0;
      next = waiting.shift()
    ) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const one = await sendOne(next);
        outcome.created.push(...one.created);
        outcome.skipped.push(...one.skipped);
        outcome.failed.push(...one.failed);
        onSettled?.();
      } catch (error) {
        if (refused.length === 0) refused.push(error);
      }
    }
  };

  const sending = files.length > 0;
  if (sending) {
    batchesSending += 1;
    if (batchesSending === 1) {
      window.addEventListener('beforeunload', askBeforeLeaving);
    }
  }
  try {
    await Promise.all(
      Array.from({ length: Math.min(atOnce, files.length) }, sendInTurn)
    );
  } finally {
    if (sending) {
      batchesSending -= 1;
      if (batchesSending === 0) {
        window.removeEventListener('beforeunload', askBeforeLeaving);
      }
    }
  }
  if (refused.length > 0) throw refused[0];
  return outcome;
}

export const taxProfileKey = (
  clientId: number,
  entityId?: number | null,
  taxYear?: number | null
) => {
  const query = new URLSearchParams();
  if (entityId) query.set('entityId', String(entityId));
  if (taxYear) query.set('taxYear', String(taxYear));
  const suffix = query.toString();
  return `${baseRoute}/client/${clientId}/profile${suffix ? `?${suffix}` : ''}`;
};

export const taxDocumentsKey = (clientId: number) =>
  `${baseRoute}/client/${clientId}/documents`;

/**
 * Matches the client's documents list and every cached summary of one of its documents
 * (taxDocumentReviewKey), both of which start with taxDocumentsKey.
 */
const matchesDocumentsKey = (clientId: number) => (key: unknown) =>
  typeof key === 'string' && key.startsWith(taxDocumentsKey(clientId));

export const taxDocumentReviewKey = (clientId: number, documentId: number) =>
  `${baseRoute}/client/${clientId}/documents/${documentId}/review`;

/** The route of one profile value, named by its key in either catalog. */
const fieldRoute = (clientId: number, key: TaxProfileWritableKey) =>
  `${baseRoute}/client/${clientId}/profile/fields/${encodeURIComponent(key)}`;

/**
 * Matches every cached read of this client's profile, whatever entity and tax year it was asked
 * for, so a write refreshes the year on screen and any other year already in the cache.
 *
 * SWR's mutate takes such a filter function where it also takes a plain key.
 */
export const matchesProfileKey = (clientId: number) => (key: unknown) =>
  typeof key === 'string' &&
  key.startsWith(`${baseRoute}/client/${clientId}/profile`);

/**
 * The body of a 409 from a profile write.
 *
 * Expects whatever the write threw. utils/api.ts throws an Error carrying the response's status and
 * body (ApiRequestError), and a 409's body names the field as it stands now.
 *
 * Returns that body, which is what the page offers to keep or to overwrite, and null when the
 * request failed for any other reason.
 */
export const conflictFromError = (
  error: unknown
): TaxProfileFieldConflictResponse | null => {
  const answer = error as { status?: number; payload?: unknown };
  if (answer?.status !== 409) return null;
  const payload = answer.payload as TaxProfileFieldConflictResponse | null;
  return payload?.current ? payload : null;
};

/**
 * True for the answers from the api that go to Rollbar: 500 and above, 403 and 404.
 *
 * 500 and above is the api failing at something it was asked to do. A 403 or a 404 on a route this
 * page built itself means the page asked for a client, a document or a field the person may not
 * have or that is not there, which nobody finds out about otherwise. A 409 is left out, because two
 * people editing the same value is what that answer is for and the page already offers to keep or
 * to overwrite the value (conflictFromError above). A request that came back with no status never
 * reached the api, so there is nothing to tell the api's owners.
 */
export const reportableApiStatus = (
  status: number | undefined
): status is number =>
  status !== undefined && (status >= 500 || status === 403 || status === 404);

/**
 * The api's own sentence out of a refused answer's body, and nothing else from that body.
 *
 * Every taxProfile route answers `{ error: '<sentence>' }` (api/routers/taxProfileRouter.ts), and
 * utils/api.ts also builds an `error` object carrying a `message`, so both shapes are read here.
 *
 * Returns that sentence, or undefined when the body holds neither shape. Nothing else is taken out
 * of the body, because the body of a refused profile write carries the client's values.
 */
export const apiErrorSentence = (payload: unknown): string | undefined => {
  const answered = (payload ?? {}) as { error?: unknown };
  if (typeof answered.error === 'string') return answered.error;
  const nested = (answered.error ?? {}) as { message?: unknown };
  return typeof nested.message === 'string' ? nested.message : undefined;
};

/** What a Rollbar item about a refused request names besides the route and the status. */
type FailedRequestContext = {
  clientId: number;
  documentId?: number;
  fieldKey?: TaxProfileWritableKey;
};

/**
 * Refreshes what every write to a profile value changes: the client's profile in every tax year
 * the cache holds, the documents list, and every document summary the cache holds.
 *
 * The documents list carries each row's waitingCount and a summary carries each line's status,
 * and every write can move both, so they are read again as well.
 *
 * Exported for a caller that sends several writes with { refresh: false } (reviewListRow in
 * pages/TaxProfile/useProfileFieldWrites.tsx) and refreshes once after the last of them.
 */
export const refreshAfterFieldWrite = async (clientId: number) => {
  await Promise.all([
    mutate(matchesProfileKey(clientId)),
    mutate(matchesDocumentsKey(clientId)),
  ]);
};

/**
 * The options of editField and review. `refresh: false` skips the refreshAfterFieldWrite each
 * of them runs after its request, for a caller that sends several writes and refreshes once.
 */
export type FieldWriteOptions = { refresh?: boolean };

/** True while the document's reading has been asked for and has not finished. */
export const documentIsBeingRead = (
  document: Pick<TaxDocumentListItem, 'extraction_status'>
): boolean =>
  document.extraction_status === 'queued' ||
  document.extraction_status === 'processing';

/**
 * How often SWR fetches the documents list again.
 *
 * Expects the latest documents response. A document whose reading has been asked for and has not
 * finished changes on its own, so the list is fetched again every DOCUMENTS_POLL_MS while at least
 * one document is in that state.
 *
 * Returns 0, which stops SWR's timer, when no document is being read.
 */
export const documentsRefreshInterval = (
  latest?: TaxDocumentsResponse
): number =>
  (latest?.documents ?? []).some(documentIsBeingRead) ? DOCUMENTS_POLL_MS : 0;

function useTaxProfile() {
  const rollbar = useRollbar();

  /**
   * Sends one Rollbar item for a request the api refused, where reportableApiStatus says so.
   *
   * Expects the method, the route, the status, the answer's body and the client id with the
   * document id or field key the request was about.
   *
   * The item's message names the method, the route and the status, and the object under it names
   * the client and the document or the field, so an item can be read without opening the page it
   * came from.
   */
  const reportAnswer = (
    method: string,
    route: string,
    status: number | undefined,
    payload: unknown,
    context: FailedRequestContext
  ) => {
    if (!reportableApiStatus(status)) return;
    rollbar.error(`[taxProfile] ${method} ${route} answered ${status}`, {
      ...context,
      status,
      error: apiErrorSentence(payload),
    });
  };

  /** The same as reportAnswer, for a caller holding the error utils/api.ts threw. */
  const reportFailure = (
    method: string,
    route: string,
    error: unknown,
    context: FailedRequestContext
  ) => {
    const { status, payload } = (error ?? {}) as ApiRequestError;
    reportAnswer(method, route, status, payload, context);
  };

  /**
   * Runs one write, sends a Rollbar item where reportableApiStatus says so, and rethrows whatever
   * the write threw, so every caller's toast and its 409 handling work unchanged.
   */
  const reporting = async <T>(
    method: string,
    route: string,
    context: FailedRequestContext,
    request: () => Promise<T>
  ): Promise<T> => {
    try {
      return await request();
    } catch (error) {
      reportFailure(method, route, error, context);
      throw error;
    }
  };

  /**
   * The profile of one legal entity for one tax year.
   *
   * Returns SWR's { data, error, isLoading } for TaxProfileResponse.
   *
   * keepPreviousData makes SWR hand back the answer for the previous key while the answer for a new
   * entity or tax year is on its way, with isLoading true until it arrives. Without it, data is
   * undefined for that time, TaxProfile.tsx draws its one-line loading state in place of the whole
   * page, the scrolling <main> of DashboardWrapper.tsx becomes one line tall, the browser moves its
   * scroll position to the top, and the page stays there when the new answer is drawn.
   *
   * `skip: true` hands SWR a null key, so nothing is fetched and data stays undefined: a document's
   * summary (ReviewModal) with no line to decide has no use for the profile. queries.ts types the
   * endpoint as a string, so the null is cast through as getDocumentReview does below.
   */
  const getProfile = (
    clientId: number,
    entityId?: number | null,
    taxYear?: number | null,
    { skip = false }: { skip?: boolean } = {}
  ) => {
    const route = skip ? null : taxProfileKey(clientId, entityId, taxYear);
    return get<TaxProfileResponse>(route as unknown as string, {
      revalidateOnFocus: true,
      keepPreviousData: true,
      onError: (error: unknown) => {
        // Nothing is fetched for a null key, so this never runs with one.
        if (route === null) return;
        reportFailure('GET', route, error, { clientId });
      },
    });
  };

  /**
   * The client's documents list, fetched again every DOCUMENTS_POLL_MS while a document is being
   * read.
   *
   * Returns SWR's { data, error } for TaxDocumentsResponse.
   *
   * The interval is held in state and set from each response rather than passed as a function. SWR
   * accepts a number or a function for refreshInterval, but its polling effect
   * (node_modules/swr/core/dist/index.mjs, the effect whose dependencies are refreshInterval,
   * refreshWhenHidden, refreshWhenOffline and key) calls a function-valued refreshInterval once,
   * with whatever the cache holds when the effect runs, and runs again only when that function's
   * identity or the key changes, never when a response arrives. On a fresh page load the cache is
   * empty, documentsRefreshInterval answers 0, no timer is set, and the list is never fetched again
   * while a document is being read. The state changes when a document starts or stops being read,
   * which is a dependency the effect does see, so SWR sets and clears the timer.
   */
  const getDocuments = (clientId: number) => {
    // The page calls getDocuments while it renders, the way it calls getProfile and the way
    // useAccountData.ts's getAccountData is called, so the useState and useEffect below run inside
    // the page's own render. react-hooks/rules-of-hooks only recognizes a function whose name
    // starts with 'use', so the rule is turned off on both lines, as useAccountData.ts turns it off
    // for useSWR.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [refreshInterval, setRefreshInterval] = useState(0);
    const route = taxDocumentsKey(clientId);
    const documents = get<TaxDocumentsResponse>(route, {
      refreshInterval,
      onError: (error: unknown) =>
        reportFailure('GET', route, error, { clientId }),
    });
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      setRefreshInterval(documentsRefreshInterval(documents.data));
    }, [documents.data]);
    return documents;
  };

  /**
   * Every value one document's latest reading gave and what became of each, which ReviewModal
   * draws.
   *
   * Expects a null documentId while no document is being reviewed. queries.ts types the endpoint as
   * a string and SWR fetches nothing for a null key, so the null is cast through as a string.
   *
   * Returns SWR's { data, error } for TaxDocumentReviewResponse.
   */
  const getDocumentReview = (clientId: number, documentId: number | null) => {
    const route =
      documentId === null ? null : taxDocumentReviewKey(clientId, documentId);
    return get<TaxDocumentReviewResponse>(route as unknown as string, {
      onError: (error: unknown) => {
        // Nothing is fetched for a null key, so this never runs with one. Both are checked because
        // the route and the document id are only ever null together.
        if (route === null || documentId === null) return;
        reportFailure('GET', route, error, { clientId, documentId });
      },
    });
  };

  const refreshProfile = async (
    clientId: number,
    entityId?: number | null,
    taxYear?: number | null
  ) => {
    await mutate(taxProfileKey(clientId, entityId, taxYear));
  };

  const refreshDocuments = async (clientId: number) => {
    await mutate(taxDocumentsKey(clientId));
  };

  /** Writes a value a person typed; answers 409 when someone else changed the value first. */
  const editField = async (
    clientId: number,
    key: TaxProfileWritableKey,
    body: EditTaxProfileFieldRequest,
    { refresh = true }: FieldWriteOptions = {}
  ) => {
    const route = fieldRoute(clientId, key);
    const answer = await reporting(
      'PUT',
      route,
      { clientId, fieldKey: key },
      () => put<TaxProfileFieldWriteResponse>(route, body)
    );
    if (refresh) await refreshAfterFieldWrite(clientId);
    return answer;
  };

  /**
   * Decides one value or many: an item naming rowId accepts that row, one naming none keeps the
   * approved value; an item's consideredRowIds close the candidates the page showed. `entityId`
   * names the legal entity the items are on; left out, the api takes the client's only legal
   * entity and answers 400 for a client with more than one.
   */
  const review = async (
    clientId: number,
    items: ReviewTaxProfileFieldsRequest['items'],
    entityId?: number | null,
    { refresh = true }: FieldWriteOptions = {}
  ) => {
    const route = `${baseRoute}/client/${clientId}/profile/review`;
    const body: ReviewTaxProfileFieldsRequest = {
      items,
      ...(entityId != null ? { entityId } : {}),
    };
    const answer = await reporting('POST', route, { clientId }, () =>
      post<ReviewTaxProfileFieldsResponse>(route, body)
    );
    if (refresh) await refreshAfterFieldWrite(clientId);
    return answer;
  };

  /**
   * Puts a stored row of the same field back into the column: the row an edit superseded (Undo) or
   * a value a document stated ("Use this value").
   */
  const applyRow = async (
    clientId: number,
    key: TaxProfileWritableKey,
    body: ApplyTaxProfileRowRequest
  ) => {
    const route = `${fieldRoute(clientId, key)}/apply-row`;
    const answer = await reporting(
      'POST',
      route,
      { clientId, fieldKey: key },
      () => post<TaxProfileFieldWriteResponse>(route, body)
    );
    await refreshAfterFieldWrite(clientId);
    return answer;
  };

  /**
   * Removes an owner (k1_partners) or an officer (entity_officers) with every value on file for
   * them: the Remove owner / Remove officer button of the person window (ListTable.tsx). `entityId`
   * names the legal entity the row is on, as on review above.
   *
   * Then refreshes what a field write refreshes, the profile and every document summary, because
   * the values the person's documents gave are gone with the row.
   */
  const removePerson = async (
    clientId: number,
    table: TaxProfilePersonTable,
    rowId: number,
    entityId?: number | null
  ) => {
    const route = `${baseRoute}/client/${clientId}/profile/people/${table}/${rowId}`;
    const body: RemoveTaxProfilePersonRequest =
      entityId != null ? { entityId } : {};
    const answer = await reporting('DELETE', route, { clientId }, () =>
      fetchDelete<RemoveTaxProfilePersonResponse>(route, body)
    );
    await refreshAfterFieldWrite(clientId);
    return answer;
  };

  /**
   * Attaches files that already sit in the client's team, by the drive item ids the pick tab gives.
   *
   * Expects one request body per file and, optionally, onProgress, which is called for each file as
   * its own request starts and again as that file settles.
   *
   * Returns the answers added up into one (AddTaxDocumentsOutcome), so the caller shows one summary
   * however many files it sent. It throws the first refusal of the whole batch (an unknown legal
   * entity, 400 or 404), exactly as a single request would, and once one is thrown no further file
   * is started.
   *
   * One request per file, TEAM_DRIVE_ADDS_AT_ONCE at a time. The route takes one file, and copying
   * that file is not something OneDrive does on its own: copyDriveItemToFolder in
   * api/src/oneDrive/apiHelpers.ts downloads the bytes through the api process and uploads them
   * again, so one request per file is what keeps any single request short.
   *
   * A file the client already has a row for is answered 409 by the route, and that one file goes
   * into `skipped` rather than failing the others. A 429 means the request was turned away before
   * the api copied anything, so it is sent again after retryWaitMs, for as long as the time since
   * its first send stays within TAX_DOCUMENT_MAX_BUSY_WAIT_MS, and then goes into `failed` as
   * 'busy'. A 503 goes
   * into `failed` at once as 'server_error' (TAX_DOCUMENT_RETRY_DELAYS_MS says why it is not sent
   * again), and so does a request that got no answer ('connection_dropped' says why). A Rollbar
   * item is sent for the last answer only, not for each retry.
   */
  const addFromTeamDrive = async (
    clientId: number,
    files: AddTaxDocumentFromTeamDriveRequest[],
    onProgress?: (progress: TeamDriveFileProgress) => void
  ): Promise<AddTaxDocumentsOutcome> => {
    const route = `${baseRoute}/client/${clientId}/documents/from-team-drive`;

    const attempt = async (
      body: AddTaxDocumentFromTeamDriveRequest,
      retry: number,
      firstSentAt = Date.now()
    ): Promise<AddTaxDocumentsOutcome> => {
      try {
        const answer = await post<AddTaxDocumentsResponse>(route, body);
        // The route takes one file, so its answer is about this file alone. A row in `created`
        // means the file was added, and anything else means it was not.
        if (answer.created.length > 0) {
          onProgress?.({ itemId: body.itemId, state: 'added' });
        } else {
          const reason = answer.skipped[0]?.reason;
          onProgress?.({
            itemId: body.itemId,
            state: 'not_added',
            ...(reason ? { reason } : {}),
          });
        }
        return answeredOutcome(answer);
      } catch (error) {
        const { status, retryAfter } = error as ApiRequestError;
        if (status === 409) {
          onProgress?.({
            itemId: body.itemId,
            state: 'not_added',
            reason: 'already_attached',
          });
          return answeredOutcome({
            created: [],
            skipped: [{ name: body.name, reason: 'already_attached' }],
          });
        }
        // No status is no answer: utils/api.ts throws a plain Error when the connection dropped or
        // the request timed out.
        const noAnswer = status === undefined;
        const busy = status === 429;
        const waitMs = busy ? retryWaitMs(retryAfter, retry) : 0;
        const elapsedMs = Date.now() - firstSentAt;
        if (busy && elapsedMs + waitMs <= TAX_DOCUMENT_MAX_BUSY_WAIT_MS) {
          await wait(waitMs);
          return attempt(body, retry + 1, firstSentAt);
        }
        reportFailure('POST', route, error, { clientId });
        onProgress?.({ itemId: body.itemId, state: 'not_added' });
        if (noAnswer) return failedOutcome(body.name, 'connection_dropped');
        if (busy) return failedOutcome(body.name, 'busy');
        if (status >= 500) return failedOutcome(body.name, 'server_error');
        throw error;
      }
    };

    const sendOne = (body: AddTaxDocumentFromTeamDriveRequest) => {
      onProgress?.({ itemId: body.itemId, state: 'sending' });
      return attempt(body, 0);
    };

    return sendOneRequestPerFile(files, TEAM_DRIVE_ADDS_AT_ONCE, sendOne);
  };

  /**
   * Sends files from the person's own computer, all under the one type and to the one legal
   * entity. A null entityId sends none, for a client with no legal entity yet. onFileSettled, when
   * given, is called each time one file's outcome is known, so the caller can say how many are
   * done.
   *
   * One request per file, TAX_DOCUMENT_UPLOADS_AT_ONCE at a time, with the answers added up into
   * one as addFromTeamDrive does. The route holds a request's whole body in the api's memory and
   * takes one file per request (TAX_DOCUMENT_MAX_FILES), so a batch never sits in the api at once.
   * Each request's FormData refers to the File the person chose rather than copying it, and a retry
   * builds a new FormData over the same File.
   *
   * A file the route refuses as too large (413) goes into `skipped` as 'too_large' rather than
   * failing the others; the modal already holds such a file back, so this is the case where the two
   * disagree. A 429 is sent again as addFromTeamDrive does, after the Retry-After the route sends
   * when every upload slot of its api process is taken. A 503 is not, for the same reason as
   * there: App Engine can send it after the api stored the file and wrote its row, and a second
   * upload would be stored beside the first as "name 1"; it goes into `failed` as 'server_error'. A
   * request that got no answer is not sent again either: it goes into `failed` as
   * 'upload_unconfirmed' (TaxDocumentAddFailureReason says why).
   * Any other refusal (no team, 409; an unknown legal entity, 400 or 404) is about the whole batch
   * and is thrown, and once one is thrown no further file is started.
   */
  const uploadDocuments = async (
    clientId: number,
    files: File[],
    docType: OcrDocumentType,
    entityId: number | null = null,
    onFileSettled?: () => void
  ): Promise<AddTaxDocumentsOutcome> => {
    const route = `${baseRoute}/client/${clientId}/documents/upload`;

    const sendOne = async (
      file: File,
      retry = 0,
      firstSentAt = Date.now()
    ): Promise<AddTaxDocumentsOutcome> => {
      const form = new FormData();
      form.append('files', file);
      form.append('docType', docType);
      if (entityId != null) form.append('entityId', String(entityId));
      let response: Response;
      try {
        // Five minutes, not the 60 s this used to have: the timer covers sending the body as well
        // as the api's work, and a 14 MB file on a slow uplink alone can take most of a minute to
        // send.
        // Past the timer the browser cannot tell whether the file arrived (upload_unconfirmed).
        response = await postFiles(route, form, { timeout: 300000 });
      } catch {
        return failedOutcome(file.name, 'upload_unconfirmed');
      }
      if (response.ok) {
        return answeredOutcome(
          (await response.json()) as AddTaxDocumentsResponse
        );
      }
      // postFiles hands back the raw response instead of throwing, so this is the one function here
      // that reads the status off a response.
      const { status } = response;
      const busy = status === 429;
      // Read only for a 429: the headers of any other answer are never looked at.
      const waitMs = busy
        ? retryWaitMs(response.headers.get('Retry-After'), retry)
        : 0;
      const elapsedMs = Date.now() - firstSentAt;
      if (busy && elapsedMs + waitMs <= TAX_DOCUMENT_MAX_BUSY_WAIT_MS) {
        await wait(waitMs);
        return sendOne(file, retry + 1, firstSentAt);
      }
      // The body is parsed inside a catch because a 500 the api did not answer itself comes back as
      // express's HTML page, and parsing that would throw before anything was reported.
      const refused = (await response.json().catch(() => null)) as {
        error?: string;
      } | null;
      reportAnswer('POST', route, status, refused, { clientId });
      if (status === 413) {
        return answeredOutcome({
          created: [],
          skipped: [{ name: file.name, reason: 'too_large' }],
        });
      }
      if (busy) return failedOutcome(file.name, 'busy');
      if (status >= 500) return failedOutcome(file.name, 'server_error');
      throw new Error(refused?.error ?? 'The upload did not go through');
    };

    return sendOneRequestPerFile(
      files,
      TAX_DOCUMENT_UPLOADS_AT_ONCE,
      (file) => sendOne(file),
      onFileSettled
    );
  };

  const readDocument = (
    clientId: number,
    documentId: number,
    body: ReadTaxDocumentRequest = {}
  ) => {
    const route = `${baseRoute}/client/${clientId}/documents/${documentId}/read`;
    return reporting('POST', route, { clientId, documentId }, () =>
      post<{ document: TaxDocumentListItem }>(route, body)
    );
  };

  /**
   * Answers where a held document belongs: keep_here records it on
   * the legal entity it is filed under, move moves it to entityId and records it there.
   *
   * Then refreshes what a field write refreshes, the profile in every entity and tax year the cache
   * holds and the documents list with every document summary, because the answer records the
   * document's values and a move changes the entity the document is filed under. It refreshes when
   * the api refuses the answer too: a 409 means somebody else answered first, and the page then
   * shows what they decided.
   */
  const answerDocumentEntity = async (
    clientId: number,
    documentId: number,
    body: AnswerTaxDocumentEntityRequest
  ) => {
    const route = `${baseRoute}/client/${clientId}/documents/${documentId}/entity-answer`;
    try {
      return await reporting('POST', route, { clientId, documentId }, () =>
        post<{ document: TaxDocumentListItem }>(route, body)
      );
    } finally {
      await refreshAfterFieldWrite(clientId);
    }
  };

  const updateDocument = (
    clientId: number,
    documentId: number,
    body: UpdateTaxDocumentRequest
  ) => {
    const route = `${baseRoute}/client/${clientId}/documents/${documentId}`;
    return reporting('PATCH', route, { clientId, documentId }, () =>
      patch<{ document: TaxDocumentListItem }>(route, body)
    );
  };

  /** Where one document's stored copy can be opened, which the page opens in a new tab. */
  const getDocumentLink = (clientId: number, documentId: number) => {
    const route = `${baseRoute}/client/${clientId}/documents/${documentId}/link`;
    return reporting('GET', route, { clientId, documentId }, () =>
      apiGet<{ url: string }>(route)
    );
  };

  return {
    getProfile,
    getDocuments,
    getDocumentReview,
    getDocumentLink,
    refreshProfile,
    refreshDocuments,
    editField,
    review,
    applyRow,
    removePerson,
    addFromTeamDrive,
    uploadDocuments,
    readDocument,
    answerDocumentEntity,
    updateDocument,
  };
}

export default useTaxProfile;
